/**
 * FoundationsRedeem — the one page a supported recipient sees.
 *
 * Product tone (Michael, Feb 2026):
 *   "The moment they receive this must feel like someone thought of
 *    them specifically. Not a shop, not a sign-up. A gift already
 *    made, with their name on it."
 *
 * Flow:
 *   1. Land on /foundations/redeem?code=… (or blank)
 *   2. Type/paste the code → we check it silently
 *   3. Green welcome — reveal the "kind donor / partner" line
 *   4. If already registered → password only → redeem → straight into Foundations
 *      If new → email + password + first name → redeem → straight into Foundations
 *   5. Session stored, /foundations lands with entitlement active
 *
 * Every text input has a data-testid; every state transition is
 * accessible via ARIA live-region updates.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const TOK = {
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.4)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.9)",
  emerald: "#7bbf9a",
};

const HERO_BG =
  "radial-gradient(ellipse at 50% 30%, rgba(19,31,56,0.9) 0%, rgba(3,7,16,0.98) 60%, rgba(3,7,16,1) 100%)";

// Cheap client-side pre-check so we don't hit the API with obvious rubbish.
function looksLikeCode(raw) {
  if (!raw) return false;
  const parts = raw.trim().toLowerCase().replace(/\s+/g, "-").split("-").filter(Boolean);
  if (parts.length < 4) return false;
  const last = parts[parts.length - 1];
  return /^\d{4}$/.test(last);
}

export default function FoundationsRedeem() {
  const nav = useNavigate();
  const [sp] = useSearchParams();

  const [code, setCode] = useState(sp.get("code") || "");
  const [checkState, setCheckState] = useState("idle"); // idle | checking | valid | invalid
  const [checkInfo, setCheckInfo] = useState(null);
  const [error, setError] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Auto-check when the URL brought us a code.
  useEffect(() => {
    const initial = sp.get("code");
    if (initial) doCheck(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doCheck(raw) {
    const cleaned = (raw || "").trim();
    setError(null);
    if (!looksLikeCode(cleaned)) {
      setCheckState("invalid");
      setCheckInfo({ reason: "malformed" });
      return;
    }
    setCheckState("checking");
    try {
      const r = await axios.post(`${API}/api/foundations/vouchers/check`, { code: cleaned });
      if (r.data?.valid) {
        setCheckState("valid");
        setCheckInfo(r.data);
      } else {
        setCheckState("invalid");
        setCheckInfo(r.data);
      }
    } catch (_) {
      setCheckState("invalid");
      setCheckInfo({ reason: "network" });
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting || checkState !== "valid") return;
    setError(null);
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/foundations/vouchers/redeem`, {
        code: code.trim(),
        email: email.trim(),
        password: password || undefined,
        name: name.trim() || undefined,
      });
      // Store the token — reuses the same key the rest of the app expects.
      if (r.data?.token) {
        localStorage.setItem("reclaim_token", r.data.token);
      }
      // Immediately land the recipient inside Foundations.
      nav("/foundations", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "unknown_error";
      const msg =
        detail === "code_already_redeemed" ? "This code has already been redeemed." :
        detail === "code_expired" ? "This code has expired — please ask your donor or organisation for a fresh one." :
        detail === "code_not_found" ? "We couldn't find that code. Check for typos and try again." :
        detail === "malformed_code" ? "That doesn't look quite right — check the whole code including the four digits at the end." :
        detail === "password_required_for_new_account" ? "Please set a password so you can come back later." :
        "Something went wrong. Please try again in a moment.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const invalidReasonLabel = useMemo(() => {
    if (checkState !== "invalid" || !checkInfo) return null;
    switch (checkInfo.reason) {
      case "malformed":       return "That doesn't look quite right — three warm words and four digits at the end.";
      case "not_found":       return "We couldn't find that code. Check for typos and try again.";
      case "already_redeemed": return "This code has already been redeemed.";
      case "expired":         return "This code has expired — please ask your donor or organisation for a fresh one.";
      case "network":         return "Network hiccup. Please try again.";
      default:                return "That code isn't valid right now.";
    }
  }, [checkState, checkInfo]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-5 py-12"
      style={{ background: HERO_BG, color: TOK.cream, fontFamily: '"Manrope", sans-serif' }}
      data-testid="foundations-redeem-page"
    >
      <div className="w-full max-w-md relative">
        <div
          className="text-[0.6rem] uppercase text-center mb-6"
          style={{ color: TOK.goldSoft, letterSpacing: "0.36em" }}
        >
          ✦  Soulumo Foundations  ✦
        </div>

        <h1
          className="italic leading-tight text-center"
          style={{
            fontFamily: '"Playfair Display", "Cormorant Garamond", serif',
            fontSize: "clamp(1.9rem, 6vw, 2.6rem)",
            fontWeight: 300,
          }}
        >
          A gift, made for you.
        </h1>
        <p
          className="mt-4 italic text-center max-w-sm mx-auto"
          style={{
            fontFamily: '"Playfair Display", serif',
            color: TOK.creamDim,
            fontSize: "clamp(1rem, 3.2vw, 1.1rem)",
            lineHeight: 1.55,
          }}
        >
          Someone paid so you could have this. Enter your code below to
          open eight installations — for morning, rest, and every day
          in between.
        </p>

        <form onSubmit={submit} className="mt-9 space-y-4">
          <label className="block">
            <span
              className="text-[0.6rem] uppercase block mb-1.5"
              style={{ color: TOK.goldSoft, letterSpacing: "0.28em" }}
            >
              Your gift code
            </span>
            <input
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value); if (checkState !== "idle") setCheckState("idle"); }}
              onBlur={() => doCheck(code)}
              autoComplete="off"
              placeholder="quiet-lantern-morning-4821"
              data-testid="foundations-redeem-code-input"
              className="w-full rounded-2xl px-4 py-3.5 italic"
              style={{
                background: "rgba(245,236,217,0.06)",
                border: `1px solid ${checkState === "valid" ? TOK.emerald : checkState === "invalid" ? "rgba(224,122,122,0.5)" : "rgba(224,169,122,0.28)"}`,
                color: TOK.cream,
                fontSize: "1rem",
                fontFamily: '"Playfair Display", serif',
                letterSpacing: "0.02em",
              }}
              aria-invalid={checkState === "invalid"}
            />
            <div className="mt-2 min-h-[1.25rem] text-[0.78rem] italic" aria-live="polite" data-testid="foundations-redeem-code-status">
              {checkState === "checking" && (
                <span style={{ color: TOK.creamMist }}>Checking gently…</span>
              )}
              {checkState === "valid" && (
                <span style={{ color: TOK.emerald }}>
                  ✓ Gifted by {checkInfo?.gifted_by || "a kind donor"}.
                </span>
              )}
              {checkState === "invalid" && (
                <span style={{ color: "rgba(255,190,190,0.85)" }}>{invalidReasonLabel}</span>
              )}
            </div>
          </label>

          {/* Second-stage inputs — appear only once the code is valid. */}
          {checkState === "valid" && (
            <div className="space-y-4" data-testid="foundations-redeem-second-stage">
              <label className="block">
                <span className="text-[0.6rem] uppercase block mb-1.5" style={{ color: TOK.goldSoft, letterSpacing: "0.28em" }}>
                  Your email
                </span>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="foundations-redeem-email"
                  className="w-full rounded-2xl px-4 py-3.5 italic"
                  style={{
                    background: "rgba(245,236,217,0.06)",
                    border: "1px solid rgba(224,169,122,0.28)",
                    color: TOK.cream,
                    fontSize: "1rem",
                    fontFamily: '"Playfair Display", serif',
                  }}
                />
              </label>
              <label className="block">
                <span className="text-[0.6rem] uppercase block mb-1.5" style={{ color: TOK.goldSoft, letterSpacing: "0.28em" }}>
                  Set a password  <span style={{ color: TOK.creamMist }}>· so you can come back</span>
                </span>
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="foundations-redeem-password"
                  className="w-full rounded-2xl px-4 py-3.5"
                  style={{
                    background: "rgba(245,236,217,0.06)",
                    border: "1px solid rgba(224,169,122,0.28)",
                    color: TOK.cream,
                    fontSize: "1rem",
                    fontFamily: '"Playfair Display", serif',
                    letterSpacing: "0.15em",
                  }}
                />
              </label>
              <label className="block">
                <span className="text-[0.6rem] uppercase block mb-1.5" style={{ color: TOK.goldSoft, letterSpacing: "0.28em" }}>
                  Your first name  <span style={{ color: TOK.creamMist }}>· optional</span>
                </span>
                <input
                  type="text"
                  autoComplete="given-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="foundations-redeem-name"
                  className="w-full rounded-2xl px-4 py-3.5 italic"
                  style={{
                    background: "rgba(245,236,217,0.06)",
                    border: "1px solid rgba(224,169,122,0.28)",
                    color: TOK.cream,
                    fontSize: "1rem",
                    fontFamily: '"Playfair Display", serif',
                  }}
                />
              </label>
            </div>
          )}

          {error && (
            <div
              className="rounded-xl px-4 py-3 text-[0.85rem] italic"
              style={{ background: "rgba(224,122,122,0.12)", border: "1px solid rgba(224,122,122,0.3)", color: "rgba(255,200,200,0.95)" }}
              role="alert"
              data-testid="foundations-redeem-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || checkState !== "valid" || !email || !password}
            data-testid="foundations-redeem-submit"
            className="w-full rounded-full mt-2"
            style={{
              background: (submitting || checkState !== "valid" || !email || !password)
                ? "rgba(224,169,122,0.16)"
                : "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              border: "1px solid #f5e0c5",
              color: (submitting || checkState !== "valid" || !email || !password) ? TOK.gold : "#1a0f05",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.86rem",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              padding: "16px 28px",
              cursor: (submitting || checkState !== "valid") ? "default" : "pointer",
              opacity: submitting ? 0.75 : 1,
            }}
          >
            {submitting ? "Opening…" : "Open my Foundations"}
          </button>

          <p
            className="text-center italic text-[0.8rem] max-w-sm mx-auto"
            style={{ color: TOK.creamMist, fontFamily: '"Playfair Display", serif' }}
          >
            No card details. Ever. This gift is yours.
          </p>
        </form>
      </div>
    </div>
  );
}
